import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  forwardRef,
  input,
  model,
  ModelSignal,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { TooltipDirective } from 'src/app/shared/directives/tooltip/tooltip.directive';
import { EskTool, ESKTOOL_TOKEN } from '../../util/esk-tool-interface';
import { Collection, Feature, MapBrowserEvent, Map as olMap } from 'ol';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Draw, Select } from 'ol/interaction';
import { delay, fromEvent, map } from 'rxjs';
import { DrawEvent } from 'ol/interaction/Draw';
import { filterNil } from 'ngxtension/filter-nil';
import { Circle } from 'ol/geom';
import { createSegmentPolygons } from './utils';
import { SelectEvent } from 'ol/interaction/Select';
import { click, altKeyOnly, shiftKeyOnly } from 'ol/events/condition';

import {
  CalculateArea,
  Intersect,
  Union,
} from 'src/app/shared/helpers/transformations';
import { DifferenceToolDirective } from '../difference/difference.directive';
import { IntersectToolDirective } from '../intersect-tool/intersect.directive';

@Component({
  selector: 'pivot-draw',
  host:{
    class: 'flex gap-2'
  },
  imports: [CommonModule, TooltipDirective, DifferenceToolDirective, IntersectToolDirective],
  providers: [
    {
      provide: ESKTOOL_TOKEN,
      useExisting: forwardRef(() => PivotDrawComponent),
    },
  ],
  template: `
    <button
      appTooltip="Draw a pivot Circle"
      [ngClass]="{ 'esk-tool--active': active() === true }"
      (click)="active.set(!active())"
      class="esk-tool"
    >
      <i class="fa fa-pie-chart"></i>
    </button>

    @if(active())
{
  <div class="border-l-2 px-2 border-gray-500 flex gap-2 justify-center items-center">
    <button   appTooltip="Create polygon" [disabled]="disabled()" class="esk-tool text-emerald-500" (click)="confirm()">
      <span class="fa fa-check"></span>
    </button>
    <button  appTooltip="Clear Pivots" class="esk-tool text-red-500" (click)="clear()">
      <i class="fa fa-trash"></i>
    </button>
    <ng-container
  intersectTool
  [intersectTargetLayers]="intersectTargetLayers()"
  [intersectTargetFeatures]="intersectTargetFeatures()"
  ></ng-container>
    <ng-container
  differenceTool
  [differenceTargetLayers]="differenceTargetLayers()"
  ></ng-container>
</div>


}
  @if(!disabled())
    {
      <div class="absolute -inset-x-10 top-12 flex  pointer-events-none z-[99]">
            <span class="rounded bg-gray-800/80 px-3 py-1 text-xs text-white shadow">
            Hold <kbd class="font-semibold">Shift</kbd> + click to select or de-select segments
            </span>
        </div>
    }
  `,
})
export class PivotDrawComponent implements OnInit, EskTool, OnDestroy {
  active: ModelSignal<boolean> = model(false);
  differenceTargetLayers = input<Array<VectorLayer<any>>>([]);
  intersectTargetLayers = input<Array<VectorLayer<any>>>([]);
  intersectTargetFeatures = input<Array<Feature<any>>>([]);

  map = input.required<olMap>();
  drawEnd = output<Feature>();

  differenceTool = viewChild(DifferenceToolDirective);
  intersectTool = viewChild(IntersectToolDirective);

  disabled = signal(true);

  drawSource = new VectorSource();
  drawLayer = new VectorLayer({
    source: this.drawSource,
    zIndex: 999,
    visible: false,
  });
  drawInteraction = new Draw({
    type: 'Circle',
    source: this.drawSource,
  });

  selectSource = new VectorSource();
  selectLayer = new VectorLayer({
    source: this.selectSource,
    visible: false,
    zIndex: 999,
  });

  selectInteraction = new Select({
    layers: [this.selectLayer],
    condition: evt => click(evt) && (altKeyOnly(evt) || shiftKeyOnly(evt)),
    addCondition: shiftKeyOnly,
    removeCondition: altKeyOnly,
    toggleCondition: shiftKeyOnly
  });

  constructor() {
    this.drawInteraction.setActive(false);
    this.selectInteraction.setActive(false);

    effect(() => {
      const active = this.active();
      const map = this.map();

      if (map == null) return;

      if (!active) {
        this.drawInteraction.setActive(false);
        this.drawLayer.setVisible(false);
        this.drawSource.clear();
        this.selectInteraction.setActive(false);
        this.selectLayer.setVisible(false);
        this.selectSource.clear();

      } else {
        this.drawInteraction.setActive(true);
        this.drawLayer.setVisible(true);
      }

      this.disabled.set(true);
    });

    fromEvent<DrawEvent>(this.drawInteraction, 'drawend')
      .pipe(
        map((e) => e.feature as Feature<Circle>),
        filterNil(),
        map((feature) => ({
          center: feature.getGeometry().getCenter(),
          radius: feature.getGeometry().getRadius(),
        })),
        map(({ center, radius }) => createSegmentPolygons(center, radius, 12))
      )
      .subscribe((features) => {
        this.drawInteraction.setActive(false);
        this.drawLayer.setVisible(false);
        this.drawSource.clear();
        this.selectInteraction.getFeatures().clear();
        this.selectLayer.setVisible(true);
        this.selectInteraction.setActive(true);
        this.selectSource.addFeatures(features);

        for (let index = 0; index < features.length; index++) {
          const feature = features[index];
          this.selectInteraction.getFeatures().push(feature);
          this.selectInteraction.changed();
        }

        this.disabled.set(false);
      });
  }

  ngOnInit() {
    const map = this.map();

    if (map == null) return;

    map.addInteraction(this.drawInteraction);
    map.addInteraction(this.selectInteraction);
    map.addLayer(this.drawLayer);
    map.addLayer(this.selectLayer);
  }

  ngOnDestroy(): void {
    const map = this.map();

    if (map == null) return;

    map.removeInteraction(this.drawInteraction);
    map.removeInteraction(this.selectInteraction);
    map.removeLayer(this.drawLayer);
    map.removeLayer(this.selectLayer);
  }

  confirm()
  {
    const features = this.selectInteraction.getFeatures().getArray();

    const feature = Union(features);

    const differenceTool = this.differenceTool();

    if(differenceTool != null)
    {
      differenceTool.execute(feature);
    }

    const intersectTool = this.intersectTool();

    if(intersectTool != null)
    {
      intersectTool.execute(feature);
    }

    this.drawEnd.emit(feature);
    this.active.set(false);
  }


  clear()
  {
    this.drawSource.clear();
    this.disabled.set(true)

    if(this.selectInteraction.getActive())
    {
      this.selectSource.clear();
      this.selectInteraction.setActive(false)
      this.selectLayer.setVisible(false);
      this.selectInteraction.getFeatures().clear();
      this.drawInteraction.setActive(true);
      this.drawLayer.setVisible(true);
    }

  }
}
